import { computed, ref } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import {
  MSE_PRODUCT,
  SEARCH_TYPES,
  productKeyMap,
  searchKeyMap,
  type MseProduct,
} from '@/types/home/product';
import ICON from '@/assets/icon/products';
import { useFindSolutions } from '../find-solutions/use-find-solutions';
import { getExamples } from './use-examples';
import { useFormulationUpload } from '../formulation/upload/use-upload';
import { useFormulationConversation } from '../formulation/use-conversation';
import { useConversation as useReConversation } from '@/components/pages/reverse-engineering/composables/use-conversation';

import { injectService } from '@patsnap/tahiti-core';
import { HomeMaterialApi } from '@/domains/home/apis/home-material-api';
import { useFormulationLegacy } from '../formulation/use-formulation-legacy';
import { useLoginSdk } from '../use-loginsdk';
import { i18n } from '@/infrastructure/locales';
import { isUserLogin } from '@eureka/utils/account';

export const useMseProducts = createSharedComposable(() => {
  const homeMaterialApiService = injectService<HomeMaterialApi>(HomeMaterialApi);
  const { submitPipeline: findSolutionSubmit, useExampleExtras: useFindSolutionsExamples } =
    useFindSolutions();
  const { openLoginDialog } = useLoginSdk(i18n as any);
  const { getProductExample, getProductFileExample } = getExamples();
  const { openDialog: openFormulationDialog, context_id: contextId } = useFormulationUpload();
  const { createConversationFromLanding: createFormulationConversation, handleExampleClick } =
    useFormulationConversation();
  const { submitFormulationLegacyPipeline } = useFormulationLegacy();
  const { createConversation: createReConversation } = useReConversation();

  const isSubmitting = ref(false);
  const functionIds = ref<string[]>([]);

  const iconMap: Record<MseProduct, string> = {
    FORMULATION: ICON.Formulation,
    FORMULATION_LEGACY: ICON.Formulation,
    FIND_SOLUTIONS: ICON.FindSolutions,
    SMART_SEARCH: ICON.SearchLeft,
    DOC_ANALYZER: ICON.DocumentAnalyzer,
    ADVANCED: ICON.SearchLeft,
    SUBSTANCE_INQUIRY: ICON.SubstanceInquiry,
    STRUCTURE_SEARCH: ICON.StructureSearch,
    SYNTH_SEARCH: ICON.WorkflowSearch,
    PROFILE_SEARCH: ICON.PropertySearch,
    COMPOSITION_SEARCH: ICON.MetalSearch,

    REVERSE_ENGINEERING: ICON.ReverseEngineering,
  };

  const getFunctionIds = async () => {
    if (functionIds.value.length === 0) {
      functionIds.value = (await homeMaterialApiService.getProducts()).map((p) => p.function_id);
    }
  };

  const getUserProducts = async () => {
    await getFunctionIds();
    const userProducts = new Set(
      functionIds.value.map((fid) => productKeyMap[fid]).filter((p) => p !== undefined),
    );

    return MSE_PRODUCT.filter((p) => userProducts.has(p));
  };

  const getUserSearches = async () => {
    await getFunctionIds();
    const userSearches = new Set(
      functionIds.value.map((fid) => searchKeyMap[fid]).filter((p) => p !== undefined),
    );
    return SEARCH_TYPES.filter((p) => userSearches.has(p));
  };

  const productWithUpload = ref<Set<MseProduct>>(
    new Set<MseProduct>(['DOC_ANALYZER', 'FORMULATION', 'FORMULATION_LEGACY']),
  );

  const usedProduct = ref<MseProduct>('FIND_SOLUTIONS');

  const setUsedProduct = (p: MseProduct) => {
    usedProduct.value = p;
  };

  const shownExamples = computed(() => {
    return getProductExample(usedProduct.value);
  });

  const shownFileExamples = computed(() => {
    return getProductFileExample(usedProduct.value);
  });

  const exampleClickCallback = (text: string, index: number) => {
    switch (usedProduct.value) {
      case 'FORMULATION': {
        handleExampleClick(text, index);
        break;
      }
      case 'FIND_SOLUTIONS': {
        useFindSolutionsExamples(text);
        break;
      }
    }
  };

  const submitInput = async (input: string) => {
    if (!input) {
      return;
    }

    isSubmitting.value = true;

    switch (usedProduct.value) {
      case 'FIND_SOLUTIONS': {
        const isLogin = await isUserLogin();
        if (!isLogin) {
          openLoginDialog();
          break;
        }

        await findSolutionSubmit(input);
        break;
      }
      case 'FORMULATION': {
        const contextIdNumber = contextId.value ? Number(contextId.value) : undefined;
        await createFormulationConversation(input, contextIdNumber);
        break;
      }
      case 'FORMULATION_LEGACY': {
        await submitFormulationLegacyPipeline(input);
        break;
      }
      case 'REVERSE_ENGINEERING': {
        await createReConversation(input);
        break;
      }
    }

    isSubmitting.value = false;
  };

  const handleUpload = async () => {
    switch (usedProduct.value) {
      case 'FORMULATION': {
        openFormulationDialog();
        break;
      }
    }
  };

  const handleExampleUpload = async (_data: Record<string, any>) => {
    switch (usedProduct.value) {
      default: {
        break;
      }
    }
  };

  return {
    iconMap,
    getUserProducts,
    getUserSearches,
    productWithUpload,
    usedProduct,
    setUsedProduct,
    shownExamples,
    shownFileExamples,
    exampleClickCallback,
    submitInput,
    isSubmitting,
    handleUpload,
    handleExampleUpload,
    getFunctionIds,
    functionIds,
  };
});
